import React, { useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import Layout from './components/Layout';
import Home from './pages/Home';
import Dashboard from './pages/Dashboard';
import AppUsers from './pages/AppUser';
import Doctors from './pages/Doctors';
import DoctorSchedule from './pages/DoctorSchedule';
import Clinics from './pages/Clinics';
import Providers from './pages/Providers';
import Appointments from './pages/Appointments';
import AppointmentDetail from './pages/AppointmentDetail';
import Patients from './pages/Patients';
import AdminUsers from './pages/AdminUsers';
import Claims from './pages/Claims';
import AgoraCall from './pages/AgoraCall';
import Login from './pages/Login';
import { useAuth } from './hooks/useAuth';
import { TokenExpirationWarning } from './components/TokenExpirationWarning';
import './i18n';
import { useTranslation } from './hooks/useTranslation';
import Settings from './pages/Settings';
import UserSettings from './pages/UserSettings';
import PublicAgoraCall from './pages/PublicAgoraCall';
import Insurance from './pages/Insurance';
import InsurancePayorDetail from './pages/InsurancePayorDetail';
import { AppointmentSSEProvider } from './lib/sse';
import { PolicyBalanceDialogProvider } from './contexts/Dialogs/PolicyBalanceDialogContext';
import { PlanDetailDialogProvider } from './contexts/Dialogs/PlanDetailDialogContext';
import { ClaimDetailDialogProvider } from './contexts/Dialogs/ClaimDetailDialogContext';
import { PermissionGuard } from './components/PermissionGuard';
import { AdminPermission } from './generated/graphql';
import dayjs from 'dayjs';
import 'dayjs/locale/en';
import 'dayjs/locale/zh-cn';
import 'dayjs/locale/zh-hk';

function App() {
  const { isAuthenticated, loading } = useAuth();
  const { t, currentLanguage } = useTranslation();

  useEffect(() => {
    const map: Record<string, string> = {
      en: 'en',
      'zh-CN': 'zh-cn',
      'zh-HK': 'zh-hk',
    };
    dayjs.locale(map[currentLanguage] || 'en');
  }, [currentLanguage]);

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh' 
      }}>
        <div className="loading"></div>
        <div style={{ marginLeft: '1rem' }}>{t('common.loading')}</div>
      </div>
    );
  }

  return (
    <>

      
      {/* Token expiration warning */}
      {isAuthenticated && <TokenExpirationWarning />}
      
      <Routes>
        {/* Public route - Login page */}
        <Route 
          path="/login" 
          element={
            isAuthenticated ? <Navigate to="/appointments" replace /> : <Login />
          } 
        />
        {/* Public route - Agora call page using query params */}
        <Route path="/public/agora-call" element={<PublicAgoraCall />} />
        
        {/* Protected routes - require authentication */}
        <Route
          path="/*"
          element={
            isAuthenticated ? (
              <ClaimDetailDialogProvider>
                <PolicyBalanceDialogProvider>
                  <PlanDetailDialogProvider>
                    <AppointmentSSEProvider>
                      <Layout>
                        <Routes>
                          <Route path="/" element={<Navigate to="/appointments" replace />} />
                          <Route path="/dashboard" element={<Dashboard />} />
                          <Route path="/users" element={<AppUsers />} />
                          <Route path="/doctors" element={<Doctors />} />
                          <Route path="/doctors/:doctorId/schedule" element={<DoctorSchedule />} />
                          <Route path="/clinics" element={<Clinics />} />
                          <Route path="/providers" element={<Providers />} />
                          <Route path="/appointments" element={<Appointments />} />
                          <Route path="/appointments/:appointmentId" element={<AppointmentDetail />} />
                          <Route path="/agora-call/:appointmentId" element={<AgoraCall />} />
                          <Route path="/patients" element={<Patients />} />
                          <Route path="/insurance" element={<Insurance />} />
                          <Route path="/insurance/:payor" element={<InsurancePayorDetail />} />
                          <Route path="/claims" element={<Claims />} />
                          <Route path="/admin-users" element={<AdminUsers />} />
                          <Route path="/settings" element={<Settings />} />
                          <Route path="/user-settings" element={<UserSettings />} />
                          {/* Catch all route for authenticated users */}
                          <Route path="*" element={<Navigate to="/appointments" replace />} />
                        </Routes>
                      </Layout>
                    </AppointmentSSEProvider>
                  </PlanDetailDialogProvider>
                </PolicyBalanceDialogProvider>
              </ClaimDetailDialogProvider>
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
      </Routes>
    </>
  );
}

export default App; 